import { useMsal } from "@azure/msal-react";
import {
    Checkbox,
    DefaultButton,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import Excel from 'assets/SVGPack/excel.svg';
import FileImportDialog from "components/controls/FileImportDialog";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { EControlType } from "components/ControlWrapper";
import saveAs from "file-saver";
import { EViewType, emitCustomEvent, fadeIn, friendlyValueToFlexibleFieldValue, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { TChangeTracker } from "global/EmitEvent/Delta";
import { IAttributeSetPayload, addUpdateAttributeSet } from "global/Reducer/dataSlice";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import { IPanelEvent, IValueChoiceGroupOption } from "model/Extension";
import {
    IBenefit as FAM_IBenefit,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IFlexibleFieldSet as FAM_IFlexibleFieldSet,
    ITierLevel as FAM_ITierLevel
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { ConnectedProps, connect, useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { EValidationState, IValidationModel } from "views/BlueRelationship/ValidationDialog/ValidationDialog.type";
import { EView } from "./GroupStructure/EView.type";
import ValidationDialog from "./ValidationDialog";
import ValidationItem from "./ValidationDialog/ValidationItem";
import { $matt_$fees_and_premiums_$bcbs_attributeset_fee_id } from "model/MasterAttributeConstants";
import { $oset_$fee_fee_type_id } from "model/ConfigOptionSetConstants";


interface IConfirm {
    tierLevels: FAM_ITierLevel[] | null;
    benefit: FAM_IBenefit | null;
    benefitName?: string;
}

export enum EGroupStructureWizardVariant {
    wizard = 1,
    export = 2,
}
interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IConfirm>) => void;

    // start condition
    defaultPayload?: IConfirm;

    setView?: any;
    variant?: 'import' | 'export'
    embedded?: boolean; // Add this prop
}

interface IViewModel {
    view: EViewState,
    importReady: boolean,
    exportReady: boolean,
    hideInfoMessages: boolean,
    processing: boolean,
}
enum EViewState {
    export, import, results
}

interface IApi {
    '$type': "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal" |
    "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal" |
    "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib" |
    "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal" |
    null

}

interface IRowApi extends IApi {
    [key: string]: string;
    '$type': "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib";
}

interface IReadSheetRequestApi extends IApi {
    '$type': "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal";
    'comment': string;
    'IsFirstRowHeader': boolean;
    'sheetName': string;
    'rows': IRowApi[];
}

// interface IUpdateSheetRequestApi extends IApi {
//     '$type': "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
//     TableName: string;
//     SheetName: string;
//     IsFirstRowHeader: true,
//     comment: string | null;
//     'Rows': IRowApi[];
// }

interface IRequestBatchApi {
    '$type': "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal";
    fileBase64: string;
    requests: IReadSheetRequestApi[]
}

export interface TLog {
    date: Date;
    type: 'warn' | 'error' | 'info' | 'ok';
    row: number | null;
    message: string;
}

export interface IGroupStructureImportWizardProps {
    isOpen?: boolean;
    onCancel?: any;
    onDismiss?: any;
    setView?: any;
    variant?: 'import' | 'export'
}

type TValidationModel = IValidationModel<FAM_IFlexibleFieldSet[]>;

interface IValidatedTransaction {
    validation?: TValidationModel
}

interface ISaveTransaction extends IValidatedTransaction {
    feesRecords: Record<string, string>[],
    logs: IValidationMessage[],
}


const FeesAndPremiumExportImportWizard = (props: IProps) => {

    const dispatch = useDispatch();

    const application = useApplication();
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);
    const state = useSelector((state: RootState) => state);

    const [view, setView] = useState<EView | null>(EView.entry);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);
    const [updateTransaction, setUpdateTransaction] = useState<ISaveTransaction | null>(null);
    const [prompts, setPrompts] = useState<IValidationMessage[]>([]);
    const [fileUpload, setFileUpload] = useState<string | null>(null);
    const categoryFeesAndPremiums = props.state.data.config.cache.map_bcbs_config_category_named["Fees and Premiums"];
    // Existing state declarations...
    const [isMounted, setIsMounted] = useState(false); // Track mounted state

    const [viewModel, setViewModel] = useState<IViewModel>({
        view: props.variant === 'export' ? EViewState.export : EViewState.import,
        importReady: false,
        exportReady: false,
        hideInfoMessages: false,
        processing: false,
    });

    const readonly = props.state.status.viewType === EViewType.ViewOnly;

    const isAdminChange = props?.state?.data?.json?.changeType?.value === IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Administrative Change(808900003)'].value;

    //Entry
    const [entryOption, setEntryOption] = useState<IValueChoiceGroupOption<EView> | null>(null);
    const entryOptions: IValueChoiceGroupOption<EView>[] = [
        { key: 'export', text: 'Export', data: EView.export, },
        { key: 'import', text: state.status.gsImportPendingExport ? 'Import (pending export)' : 'Import', data: EView.import, disabled: readonly || isAdminChange || state.status.gsImportPendingExport },
    ];

    const canImport = state.status.viewType === EViewType.Modify;


    // startup use effect
    useEffect(() => {
        // Status Initialization
        if (props.isOpen || props.embedded) { // Add embedded check

            // only export mode
            if (props.variant === "export") {
                setView(EView.export);
                setEntryOption(entryOptions.find(q => q.data === EView.export)!);
            }
            else {
                setView(EView.entry);
            }
        }

    }, [props.isOpen, props.embedded]); // Add embedded to dependencies

    // useEffect(() => {
    //     if (view === EView.import) {
    //         if (isAdminChange) {
    //             if (isMounted) { // Check if component is still mounted
    //                 setView(EView.export);
    //             }
    //         }
    //     }
    // }, [view, isMounted]); // Add isMounted to dependencies

    useEffect(() => {
        setIsMounted(true); // Set mounted state to true when effect runs

        return () => {
            setIsMounted(false); // Set mounted state to false on cleanup
        };
    }, []);

    const onRenderExportFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Download'
                styles={panelButtonsStyles}
                //disabled={!viewModel.exportReady || viewModel.processing}
                onClick={async () => {

                    (async () => {
                        try {
                            //dispatch(setProcessing(true));
                            const excelExtract = await import("global/ExcelExtract")
                            const { EditFeesExcelExtract } = excelExtract;
                            const result = await EditFeesExcelExtract(props.state, dataSource);
                            const fileName = `FeeEdit-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                            saveAs(result.file, fileName);

                            if (canImport)
                                setViewModel(t => ({ ...t, view: EViewState.import }));


                            // logs.push({
                            //     date: new Date(),
                            //     title: `Export complete.`,
                            //     type: EValidationSeverity.info,
                            // });
                        } catch (error) {
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: error
                            });
                        } finally {
                            if (isMounted) { // Ensure component is still mounted before setting state
                                // State update logic...
                                //setPrompts(t => ([...t, ...logs]));

                                setViewModel(vm => ({
                                    ...vm,
                                    processing: false,
                                }));
                            }
                        }
                    })();

                }}
            />
            <DefaultButton
                text='Cancel'
                onClick={resetPanel}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderImportFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Import Fee Edits'
                styles={panelButtonsStyles}
                //disabled={!viewModel.exportReady || viewModel.processing}
                onClick={async () => {
                    const logs: IValidationMessage[] = []
                    //alert(`IMPORTING: ${fileUpload}`);
                    console.log(fileUpload)
                    const reqJsonRead = {
                        "$type": "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal",
                        "FileBase64": fileUpload,
                        "ReadOnly": true,
                        "Requests": [
                            {
                                "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                "SheetName": "Fees And Premiums"
                            },
                        ]
                    }

                    const buildResp = await dataSource.net.raw('POST', 'api/Excel/Read', reqJsonRead);
                    const buildRespBlob: IRequestBatchApi = await buildResp.json();
                    console.log(buildRespBlob);
                    // Contacts
                    // ----------------------
                    const xlFeePremiumSheet = buildRespBlob.requests.find(q => q.sheetName === 'Fees And Premiums');
                    let xlFeePremiumRecords: Record<string, string>[] = [];
                    xlFeePremiumSheet?.rows.forEach((eachRow, eachRowIndex) => {

                        // skip first row, its the header
                        if (eachRowIndex === 0)
                            return;

                        let feeAndPremiumRecord: Record<string, string> =
                        {
                            "ID": eachRow['ID'],
                            "Fees And Premium": eachRow['Fees And Premium'],
                            "Scope": eachRow['Scope'],
                            "Status": eachRow['Status'],
                            "Category": eachRow['Category']
                        }
                        let testString = "";
                        testString += "ID " + eachRow['ID']
                        testString += "Fees And Premium " + eachRow['Fees And Premium']
                        testString += "Scope " + eachRow['Scope']
                        testString += "Status " + eachRow['Status']
                        testString += "Category " + eachRow['Category']
                        state.data.config.attributes.forEach(eachAttribute => {

                            // https://bcbsonlinedevgl.crm.dynamics.com/main.aspx?appid=bcf569be-d29b-ec11-b400-000d3a5b9435&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=f5646443-d590-ec11-b400-0022480a93ed
                            // bcbs_attributeset_fee Fee
                            // we are only doing fees attribute
                            if (eachAttribute._bcbs_masterattributesetid_value !== $matt_$fees_and_premiums_$bcbs_attributeset_fee_id)
                                return;

                            // if not part of views, ignore
                            if (!eachAttribute.bcbs_viewsortorder)
                                return;

                            testString += " " + eachAttribute.bcbs_name! + ":" + eachRow[eachAttribute.bcbs_name!]
                            feeAndPremiumRecord[eachAttribute.bcbs_name!] = eachRow[eachAttribute.bcbs_name!]

                        })
                        //DATA VALIDATIONS -----------
                        //PEPM - nothing in Fee Amount at least one value in the Coverage Level rates
                        const feeType: String = feeAndPremiumRecord["Fee Type"];
                        const feeAmountString: String = feeAndPremiumRecord["Fee Amount:"];
                        if (feeType?.includes("PEPM")) {
                            if (feeAmountString == null || feeAmountString.length === 0) {
                                //need to check all coverage level rates to check if there is any and if they are valid currency string
                                console.log("checking all fees to make sure at least one is populated - also make sure that it is proper currency")

                                let containsRateInCoverageLevel = false;
                                for (const key in feeAndPremiumRecord) {
                                    if (feeAndPremiumRecord.hasOwnProperty(key) && key.includes("Rate") && feeAndPremiumRecord[key] !== null && feeAndPremiumRecord[key] !== "") {
                                        console.log("rate stuff: ", feeAndPremiumRecord[key])
                                        containsRateInCoverageLevel = true;

                                    }
                                }
                                if (!containsRateInCoverageLevel) {
                                    logs.push({
                                        date: new Date(),
                                        title: `Fee Error ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: Fee Type 'Per Employee Per Month (PEPM) requires at least one rate field to be filled out.`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }
                        }
                        //warn about empty fee type
                        else if(feeType === "" || feeType == null){
                            logs.push({
                                date: new Date(),
                                title: `Fee warning ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: Fee Type is blank.`,
                                type: EValidationSeverity.warn,
                            });
                        }
                        //error if fee type doesnt exist
                        else if(doesFeeExist(feeType) === false){
                            logs.push({
                                date: new Date(),
                                title: `Fee Error ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: Fee Type does not exist.`,
                                type: EValidationSeverity.error,
                            });
                        }
                        
                        //error on the boolean ones if it isnt yes or no


                        //Guarantee Yes/No required
                        const guarantee = feeAndPremiumRecord["Guarantee"]
                        //If Guarantee = Yes, then Guarantee Start and End Date required
                        if (guarantee === "Yes") {
                            let startDateString = feeAndPremiumRecord['Guarantee Start Date'];
                            let endDateString = feeAndPremiumRecord['Guarantee End Date'];
                            startDateString = excelSerialDateToDateString(startDateString);
                            endDateString = excelSerialDateToDateString(endDateString);
                            if (startDateString === "" || startDateString === null) {
                                logs.push({
                                    date: new Date(),
                                    title: `Fee Error ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: Start Date String cannot be empty with Guarantee`,
                                    type: EValidationSeverity.error,
                                });
                            }
                            if (endDateString === "" || endDateString === null) {
                                logs.push({
                                    date: new Date(),
                                    title: `Fee Error ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: End Date String cannot be empty with Guarantee`,
                                    type: EValidationSeverity.error,
                                });
                            }
                            if (startDateString !== "" || startDateString !== null) {
                                const startDate = new Date(startDateString);
                                if (isNaN(startDate.getTime())) {
                                    logs.push({
                                        date: new Date(),
                                        title: `Fee Error ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: Start Date String is not valid`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }
                            if (endDateString !== "" || endDateString !== null) {
                                const endDate = new Date(endDateString);
                                if (isNaN(endDate.getTime())) {
                                    logs.push({
                                        date: new Date(),
                                        title: `Fee Error ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: End Date String is not valid`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }

                        }
                        else {
                            const startDateString = feeAndPremiumRecord['Guarantee Start Date'];
                            const endDateString = feeAndPremiumRecord['Guarantee End Date'];
                            if (startDateString !== "" && startDateString !== null) {
                                logs.push({
                                    date: new Date(),
                                    title: `Fee Error ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: Start Date String should not be populated`,
                                    type: EValidationSeverity.warn,
                                });

                            }
                            if (endDateString !== "" && endDateString !== null) {
                                logs.push({
                                    date: new Date(),
                                    title: `Fee Error ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']}: End Date String should not be populated`,
                                    type: EValidationSeverity.warn,
                                });
                            }
                        }
                        //console.log(testString)
                        // logs.push({
                        //     date: new Date(),
                        //     title: `Fee ${feeAndPremiumRecord["Fees And Premium"]} with Admin ${feeAndPremiumRecord['BCC Admin']} being updated after validations`,
                        //     type: EValidationSeverity.info,
                        // });
                        xlFeePremiumRecords.push(feeAndPremiumRecord)
                    });
                    // Post processing
                    setPrompts(t => ([...t, ...logs]));
                    updateFees({
                        feesRecords: xlFeePremiumRecords,
                        logs: logs,
                    });
                }}
            />
            <DefaultButton
                text='Cancel'
                onClick={resetPanel}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function doesFeeExist(feeType: String): boolean {
        var feeTypeOptionSetItems = state.data.config.cache.map_bcbs_config_optionsetitems_bySetId[$oset_$fee_fee_type_id];
        var doesExist = false;
        feeTypeOptionSetItems.forEach(optionSetItem => {
            if (optionSetItem.bcbs_name === feeType || feeType === "")
            {
                doesExist = true;
            }
        })
        return doesExist;
    }

    function excelSerialDateToDateString(serialString: string): string {
        // Convert the input string to a number
        const serial = parseFloat(serialString);

        // Check if the conversion resulted in a valid number
        if (isNaN(serial)) {
            return ""
        }

        // Excel's epoch is January 1, 1900. However, Excel incorrectly considers 1900 as a leap year.
        const epoch = new Date(Date.UTC(1899, 11, 30));

        // Add the serial number to the epoch to get the correct date
        const date = new Date(epoch.getTime() + serial * 24 * 60 * 60 * 1000);

        // Format the date into a string, e.g., "YYYY-MM-DD"
        const year = date.getUTCFullYear();
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
        const day = date.getUTCDate().toString().padStart(2, '0');

        // Return the formatted date string
        return `${year}-${month}-${day}`;
    }


    const updateFees = async (xSaveModel: ISaveTransaction) => {
        let saveModel: ISaveTransaction | null = { ...xSaveModel };

        const infoLogs = saveModel?.logs.filter(log => log.type === EValidationSeverity.info || log.type === EValidationSeverity.warn);
        const saveModelLogs = saveModel?.logs.filter(log => log.type === EValidationSeverity.error);
        try {
            // create validation if it does not already exists
            if (!saveModel.validation) {
                saveModel.validation = {
                    validationMessages: saveModelLogs,
                    state: EValidationState.PendingUser,
                    data: xSaveModel.validation?.data,
                }

                // validation rules
                if (saveModel.validation.validationMessages.length > 0) {
                    saveModel.validation.state = EValidationState.PendingUser;
                    return;
                }
                else {
                    saveModel.validation.state = EValidationState.Fulfilled;
                }
            }

            if (saveModel.validation && saveModel.validation.state !== EValidationState.Fulfilled)
                return;


            // dispatch(addUpdateGroupStructure({
            //     groupNumbers: saveModel.groupNumbers,
            //     cesNumbers: saveModel.cesNumbers,
            //     departments: saveModel.departments,
            // }));

            setPrompts(t => ([...t, ...infoLogs]));
            setFileUpload(null);

            setViewModel(t => ({ ...t, view: EViewState.results }));
            let update = [] as IAttributeSetPayload[];
            let changeTracker: TChangeTracker = {};

            xSaveModel.feesRecords.forEach((feeRecord) => {
                state.data.json.feesAndPremiums.forEach((eachFeesAndPremium, index) => {
                    eachFeesAndPremium.attributeSets.forEach((eachAttributeSet, index) => {
                        if (eachAttributeSet.id === feeRecord["ID"]) { //ref id is the fee config - we dont care since we have the direct id
                            const clone = structuredClone(eachAttributeSet);
                            console.log(clone)
                            clone.fields.forEach(eField => {
                                //NEED TO DO CHANGE TRACKER
                                //const matchingField = eachAttributeSet.fields.find(q => q.configRef.name === feeRecord[]);
                                for (const key in feeRecord) {
                                    if (feeRecord.hasOwnProperty(key) && key === eField.name) {
                                        const valueText = feeRecord[key];
                                        console.log("matching field: ", eField.name)
                                        console.log(" new value: ", valueText)
                                        //special ones
                                        let lookupTemplate = null as any;
                                        const attribute = state.data.config.cache.map_bcbs_config_attribute[eField.configRef.id];
                                        const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
                                        if (masterAttribute.bcbs_attributetypecode as EControlType === EControlType.lookup) {
                                            console.log("attribute: ", attribute)
                                            console.log("master: ", masterAttribute)
                                            //bccADMINS
                                            if (masterAttribute.bcbs_lookupentitylogicalname === "bcbs_bccadmin") {
                                                for (const key in state.data.config.cache.map_bcbs_bccadmin) {
                                                    if (state.data.config.cache.map_bcbs_bccadmin.hasOwnProperty(key)) {
                                                        const configCat = state.data.config.cache.map_bcbs_bccadmin[key];
                                                        if (configCat.statecode === 0 && configCat.bcbs_name === valueText) //active bccadmins - this also assumes no duplicate names
                                                        {
                                                            let entityRef: FAM_IEntityReferenceValue = {
                                                                entityType: "bcbs_bccadmin",
                                                                id: key,
                                                                name: configCat.bcbs_name,
                                                                type: "entity"
                                                            }
                                                            lookupTemplate = entityRef
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        var value = friendlyValueToFlexibleFieldValue(
                                            state.data.config,
                                            valueText,
                                            attribute,
                                            masterAttribute, lookupTemplate)
                                        // if (value) {
                                        changeTracker[eField.configRef.id] =
                                        {
                                            field: eField,
                                            fieldValue: {
                                                id: uuidv4(),
                                                tierRef: null,
                                                type: 'primary',
                                                value: value
                                            },
                                            value: value
                                        };
                                        //we might need to check if fields actually change from what they were.
                                        console.log("Value: ", value)
                                        // console.log("Change Tracker: ", changeTracker)
                                        eField.values[0].value = value;
                                        //}
                                    }
                                }
                            });
                            update.push({
                                category: categoryFeesAndPremiums as FAM_IBenefit,
                                scope: eachFeesAndPremium,
                                attributeSets: [clone],
                                config: props.state.data.config,
                                changeTracker: changeTracker
                            });
                        }
                    });
                });
            })
            // UPDATES GO HERE -----------------
            const x = update;
            debugger;
            dispatch(addUpdateAttributeSet(update));
            saveModel = null;

        } finally {
            setUpdateTransaction(saveModel);
        }
    }


    const onRenderExport = () => {
        return <Stack >
            <Separator />
            <Stack horizontal tokens={{ childrenGap: 15 }}>
                <img src={Excel} style={{ width: '60px' }} alt={"application icon"} />
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text block variant="small">
                        The process downloads all of the fees for the snapshot into an excel file.
                    </Text>
                    <Text block variant="small">
                        Afterwards, needed updates can be made to the file, and the file can be re-uploaded.
                    </Text>
                </Stack>
            </Stack>
            <div style={{ marginTop: '18px' }}>
            </div>

            {viewModel.processing &&
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <Spinner size={SpinnerSize.medium} />
                    <Text>Processing</Text>
                </Stack>
            }

            {viewModel.view === EViewState.results &&
                <Stack
                    tokens={{ childrenGap: 8 }}
                    styles={{ root: { marginTop: '15px', marginBottom: '10px' } }}
                >
                    <Separator />
                    <Stack>
                        {prompts.map((vm, index) => {
                            return <ValidationItem
                                key={index}
                                data={vm}
                            />;
                        })}
                    </Stack>
                </Stack>
            }
        </Stack>;
    }



    const onRenderImport = () => {
        return <Stack
            tokens={{ childrenGap: 8 }}
            styles={{ root: { marginTop: '15px', marginBottom: '15px' } }}
        >
            <FileImportDialog
                maxFiles={1}
                showAttached={true}
                onDropBase64={(file, name) => {
                    console.log("File received:", file, name);
                    setFileUpload(file);
                }}

            />
        </Stack>
    }

    const onRenderResults = () => {
        // const clicker = <Link onClick={(event) => {

        //     const csvSanitize = (input: string) => {
        //         const escapedStr = `"${input.replace(/"/g, '""')}"`;
        //         return escapedStr;
        //     }

        //     const messages = prompts.map(q => {
        //         return [
        //             q.date?.toISOString(),
        //             q.type,
        //             csvSanitize(q.title),
        //             csvSanitize(q.description ?? "")
        //         ].join(',')
        //     });

        //     if (messages[0] !== 'date,type,title,description')
        //         messages.unshift('date,type,title,description');

        //     const fileName = `${state.data.json.bcbs_shortname ?? state.data.snapshot?.bcbs_groupconfigurationsnapshotid}-gs-${new Date().getTime()}.log.csv`;
        //     const blob = new Blob([messages.join(`\r\n`)], { type: 'text/plan;charset=utf-8' });
        //     saveAs(blob, fileName, { autoBom: true });
        // }}>
        //     here
        // </Link>;

        return <Stack tokens={{ childrenGap: 15 }}>
            <Separator />
            <Checkbox
                label="Hide information messages"
                checked={viewModel.hideInfoMessages}
                onChange={(event, checked) => setViewModel(t => ({ ...t, hideInfoMessages: checked ?? false }))}
            />
            {/* <div>
                    <Text>Click&nbsp;{clicker}&nbsp;to download log details.</Text>
                </div> */}


            <Stack
                tokens={{ childrenGap: 8 }}
                styles={{ root: { marginTop: '15px', marginBottom: '15px' } }}
            >
                <Stack>
                    {prompts.map((vm, index) => {
                        if (viewModel.hideInfoMessages)
                            if (vm.type === EValidationSeverity.info)
                                return <></>;

                        return <ValidationItem
                            key={index}
                            data={vm}
                        />;
                    })}
                </Stack>
            </Stack>
        </Stack>
    }

    const onRenderResultsFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Done'
                styles={panelButtonsStyles}
                onClick={async () => {
                    console.log(prompts);
                    // setViewModel(x => { const c = { ...x }; c.view = EViewState.export; return c })
                    // props.onDismiss?.();
                    resetPanel()
                }}
            />
            <DefaultButton
                text='Cancel'
                disabled
                onClick={() => {
                    // setViewModel(x => { const c = { ...x }; c.view = EViewState.export; return c })
                    // props.onDismiss?.();
                    resetPanel()
                }}
            />
        </div>;
    }

    const onRenderValidationDialog = () => {
        if (updateTransaction?.validation?.state === EValidationState.PendingUser)
            return <ValidationDialog
                defaultValue={updateTransaction?.validation}
                onCancel={(xValidationModel) => {
                    setUpdateTransaction(null);
                }}
                onContinue={(xValidationModel) => {
                    const nSaveTransaction: ISaveTransaction = {
                        ...updateTransaction!,
                        validation: {
                            ...updateTransaction.validation!,
                            state: EValidationState.Fulfilled
                        }
                    }
                    updateFees(nSaveTransaction);
                }}
            />
    }

    const onRenderBody = () => {
        let onRender: (() => JSX.Element) | null = null;

        switch (viewModel.view) {
            case EViewState.export:
                onRender = onRenderExport;
                break;

            case EViewState.import:
                onRender = onRenderImport;
                break;

            case EViewState.results:
                onRender = onRenderResults;
                break;
        }

        if (onRender == null)
            throw new Error("unexpected view state.");

        return <>
            {onRender()}
            {onRenderValidationDialog()}
        </>
    }
    const onRenderFooter = () => {

        switch (viewModel.view) {
            case EViewState.export:
                return onRenderExportFooter();

            case EViewState.import:
                return onRenderImportFooter();

            case EViewState.results:
                return onRenderResultsFooter();

            default:
                throw new Error("unexpected view state.");
        }
    }

    const resetPanel = () => { props.onCancel!(); setView(EView.entry); setViewModel(t => ({ ...t, view: EViewState.export })); }

    const content = (
        <>
            {onRenderBody()}
        </>
    );

    const footer = onRenderFooter();

    // If embedded, just return the content without Panel wrapper
    if (props.embedded) {
        return (
            <div>
                {content}
                <div style={{ marginTop: '20px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
                    {footer}
                </div>
            </div>
        );
    }

    // Original Panel implementation for standalone use
    return <Panel
        isOpen={props.isOpen}
        onDismiss={resetPanel}
        type={PanelType.medium}
        layerProps={{ eventBubblingEnabled: true }}
        headerText="Export Fees || Import Fees"
        onRenderFooterContent={onRenderFooter}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(FeesAndPremiumExportImportWizard);
